/**
 * Axios HTTP Client Implementation
 * IHttpClient interface'inin Axios ile implementasyonu
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  CancelTokenSource,
  Method
} from 'axios';

import {
  IHttpClient,
  HttpRequestConfig,
  HttpResponse,
  HttpClientOptions,
  HttpClientEvents,
  IHttpCache,
  RequestMetrics
} from './httpClient.interface';

import { API_CONFIG } from '../config/api.config';
import requestInterceptorManager from './interceptors/request.interceptor';
import errorInterceptorManager from './interceptors/error.interceptor';
import authInterceptor from './interceptors/auth.interceptor';
import loggingInterceptorManager from './interceptors/logging.interceptor';
import { ApiResponse, ApiErrorResponse, isApiResponse, PaginationMeta, PaginatedResponse } from '../types/api.types';

/**
 * Simple in-memory cache implementation
 */
class MemoryCache implements IHttpCache {
  private readonly cache = new Map<string, { value: any; expiresAt: number }>();
  private readonly maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  set<T>(key: string, value: T, ttl: number = 300000): void {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, {
      value,
      expiresAt: Date.now() + ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.value as T;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }
}

/**
 * AxiosHttpClient - Axios tabanlı HttpClient implementasyonu
 */
export class AxiosHttpClient implements IHttpClient {
  private readonly instance: AxiosInstance;
  private readonly cancelTokenSources: Map<string, CancelTokenSource> = new Map();
  private readonly cache: IHttpCache;
  private readonly events: HttpClientEvents = {};
  private readonly clientOptions: HttpClientOptions;
  private readonly defaultConfig: HttpRequestConfig;

  constructor(options: HttpClientOptions = {}) {
    this.clientOptions = {
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      enableCache: true,
      maxCacheSize: 100,
      defaultCacheTTL: 5 * 60 * 1000, // 5 minutes
      enableLogging: true,
      retryCount: API_CONFIG.RETRY.COUNT,
      retryDelay: API_CONFIG.RETRY.DELAY,
      ...options
    };

    this.cache = new MemoryCache(this.clientOptions.maxCacheSize);
    this.defaultConfig = {
      timeout: this.clientOptions.timeout,
      headers: this.clientOptions.headers,
      withCredentials: this.clientOptions.withCredentials
    };

    // Axios instance oluştur
    this.instance = axios.create({
      baseURL: this.clientOptions.baseURL,
      timeout: this.clientOptions.timeout,
      headers: this.clientOptions.headers,
      withCredentials: this.clientOptions.withCredentials
    });

    // Interceptor'ları kur
    this.setupInterceptors();
  }

  // ==================== CORE METHODS ====================

  async get<T = any>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'GET',
      url
    });
  }

  getCacheKeys(): string[] {
    return this.cache.keys();
  }

  getCache<T>(key: string): T | null {
    return this.cache.get<T>(key);
  }

  async post<T = any>(url: string, data?: any, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'POST',
      url,
      data
    });
  }

  async put<T = any>(url: string, data?: any, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'PUT',
      url,
      data
    });
  }

  async patch<T = any>(url: string, data?: any, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'PATCH',
      url,
      data
    });
  }

  async delete<T = any>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'DELETE',
      url
    });
  }

  async head<T = any>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'HEAD',
      url
    });
  }

  async options<T = any>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>({
      ...config,
      method: 'OPTIONS',
      url
    });
  }

  async request<T = any>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
    // Config'i merge et
    const mergedConfig: HttpRequestConfig = {
      ...this.defaultConfig,
      ...config,
      headers: {
        ...this.defaultConfig.headers,
        ...config?.headers
      }
    };

    // Cache kontrolü
    if (mergedConfig.method?.toUpperCase() === 'GET' && mergedConfig.cacheKey) {
      const cached = this.cache.get<T>(mergedConfig.cacheKey);
      if (cached) {
        mergedConfig._cacheHit = true;
        return this.createHttpResponse<T>({
          data: cached,
          status: 200,
          statusText: 'OK (Cached)',
          headers: { 'x-cache-hit': 'true' },
          config: mergedConfig as AxiosRequestConfig
        } as AxiosResponse<T>);
      }
    }

    try {
      // Auth interceptor
      await authInterceptor.beforeRequest(mergedConfig);

      // Request interceptor
      const processedConfig = await requestInterceptorManager.intercept(mergedConfig);

      // Log request
      loggingInterceptorManager.logRequest(processedConfig);

      // Axios request
      const axiosConfig = this.convertToAxiosConfig(processedConfig);
      const response = await this.instance.request<T>(axiosConfig);

      // Log response
      loggingInterceptorManager.logResponse(response, processedConfig);

      // Auth after response
      await authInterceptor.afterResponse(response);

      // Cache'e kaydet
      if (processedConfig.cacheKey && processedConfig.method?.toUpperCase() === 'GET') {
        const ttl = processedConfig.cacheTTL || this.clientOptions.defaultCacheTTL;
        this.cache.set(processedConfig.cacheKey, response.data, ttl);
      }

      return this.createHttpResponse(response);

    } catch (error) {
      return this.handleRequestError(error, mergedConfig);
    }
  }

  // ==================== CONVENIENCE METHODS ====================

  async getApi<T = any>(url: string, config?: HttpRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.get<T>(url, config);
    
    const isApiResponseData = (data: any): data is ApiResponse<T> => {
    return data && 
           typeof data === 'object' && 
           'success' in data && 
           typeof data.success === 'boolean' &&
           'data' in data;
    };

    if (isApiResponseData(response.data)) {
      return response.data as ApiResponse<T>;
    }
    
    // Backend standart formatında dönmüyorsa wrap et
    return {
      data: response.data,
      message: 'Success',
      status: response.status,
      success: true,
      timestamp: new Date().toISOString()
    };
  }

  async getPaginated<T = any>(url: string, config?: HttpRequestConfig): Promise<PaginatedResponse<T>> {
    const response = await this.get<T[]>(url, config);
    
    const isPaginatedResponse = (data: any): data is PaginatedResponse<T> => {
    return data && 
           typeof data === 'object' && 
           'success' in data && 
           typeof data.success === 'boolean' &&
           'data' in data &&
           'meta' in data &&
           typeof data.meta === 'object';
    };
    if (isPaginatedResponse(response.data)) {
      return response.data as PaginatedResponse<T>;
    }
    
    const meta = this.extractPaginationMeta(response);
    // Paginated response wrap et
    return {
      data: response.data,
      message: 'Success',
      status: response.status,
      success: true,
      timestamp: new Date().toISOString(),
      meta: meta
    };
  }

  async postApi<T = any>(url: string, data?: any, config?: HttpRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.post<T>(url, data, config);
    
    if (isApiResponse<T>(response.data as object)) {
      return response.data as ApiResponse<T>;
    }
    
    return {
      data: response.data,
      message: 'Created successfully',
      status: response.status,
      success: true,
      timestamp: new Date().toISOString()
    };
  }

  async putApi<T = any>(url: string, data?: any, config?: HttpRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.put<T>(url, data, config);
    
    if (isApiResponse<T>(response.data as object)) {
      return response.data as ApiResponse<T>;
    }
    
    return {
      data: response.data,
      message: 'Updated successfully',
      status: response.status,
      success: true,
      timestamp: new Date().toISOString()
    };
  }

  async deleteApi<T = any>(url: string, config?: HttpRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.delete<T>(url, config);
    
    if (isApiResponse<T>(response.data as object)) {
      return response.data as ApiResponse<T>;
    }
    
    return {
      data: response.data,
      message: 'Deleted successfully',
      status: response.status,
      success: true,
      timestamp: new Date().toISOString()
    };
  }

  async upload<T = any>(url: string, formData: FormData, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    return this.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async download(url: string, config?: HttpRequestConfig): Promise<Blob> {
    const response = await this.get<Blob>(url, {
      ...config,
      responseType: 'blob'
    });
    
    return response.data;
  }

  async stream(url: string, config?: HttpRequestConfig): Promise<ReadableStream> {
    const response = await this.get<ReadableStream>(url, {
      ...config,
      responseType: 'stream'
    });
    
    return response.data;
  }

  // ==================== INTERCEPTORS ====================

  addRequestInterceptor(
    onFulfilled?: (config: HttpRequestConfig) => HttpRequestConfig | Promise<HttpRequestConfig>,
    onRejected?: (error: any) => any
  ): number {
    return this.instance.interceptors.request.use(
      async (config: any) => {
        if (onFulfilled) {
          const result = await onFulfilled(config);
          return result;
        }
        return config;
      },
      onRejected
    );
  }

  addResponseInterceptor(
    onFulfilled?: (response: HttpResponse) => HttpResponse | Promise<HttpResponse>,
    onRejected?: (error: any) => any
  ): number {
    return this.instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (onFulfilled) {
          const httpResponse = this.createHttpResponse(response);
          const result = await onFulfilled(httpResponse);
            
          // Sadece değişen alanları güncelle
          response.data = result.data;
          response.status = result.status;
          response.statusText = result.statusText || '';
            
          // Headers'ı güncelle (undefined değilse)
          if (result.headers) {
            response.headers = result.headers as any;
          }
            
          return response;
        }
        return response;
      },
      onRejected
    );
  }

  ejectInterceptor(interceptorId: number): void {
    this.instance.interceptors.request.eject(interceptorId);
    this.instance.interceptors.response.eject(interceptorId);
  }

  clearInterceptors(): void {
    this.instance.interceptors.request.clear();
    this.instance.interceptors.response.clear();
  }

  // ==================== CONFIGURATION ====================

  setBaseURL(baseURL: string): void {
    this.instance.defaults.baseURL = baseURL;
    this.clientOptions.baseURL = baseURL;
  }

  setTimeout(timeout: number): void {
    this.instance.defaults.timeout = timeout;
    this.clientOptions.timeout = timeout;
    this.defaultConfig.timeout = timeout;
  }

  setDefaultHeader(key: string, value: string): void {
    this.instance.defaults.headers.common[key] = value;
    this.defaultConfig.headers = {
      ...this.defaultConfig.headers,
      [key]: value
    };
  }

  removeDefaultHeader(key: string): void {
    delete this.instance.defaults.headers.common[key];
    if (this.defaultConfig.headers) {
      delete this.defaultConfig.headers[key];
    }
  }

  clearDefaultHeaders(): void {
    this.instance.defaults.headers.common = {};
    this.defaultConfig.headers = {};
  }

  setAuthToken(token: string | null): void {
    if (token) {
      this.setDefaultHeader('Authorization', `Bearer ${token}`);
    } else {
      this.removeDefaultHeader('Authorization');
    }
  }

  clearAuthToken(): void {
    this.removeDefaultHeader('Authorization');
  }

  // ==================== CACHE ====================

  clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  clearAllCache(): void {
    this.cache.clear();
  }

  setCacheEnabled(enabled: boolean): void {
    this.clientOptions.enableCache = enabled;
  }

  // ==================== UTILITIES ====================

  createCancelToken(): { token: any; cancel: (message?: string) => void } {
    const source = axios.CancelToken.source();
    const requestId = `cancel_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    this.cancelTokenSources.set(requestId, source);
    
    return {
      token: source.token,
      cancel: (message?: string) => {
        source.cancel(message);
        this.cancelTokenSources.delete(requestId);
      }
    };
  }

  cancelRequest(message?: string): void {
    for (const source of this.cancelTokenSources.values()) {
      source.cancel(message || 'Request cancelled by user');
    }
    this.cancelTokenSources.clear();
  }

  cancelAllRequests(): void {
    for (const source of this.cancelTokenSources.values()) {
      source.cancel('All requests cancelled');
    }
    this.cancelTokenSources.clear();
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  setRetryConfig(count: number, delay: number): void {
    this.clientOptions.retryCount = count;
    this.clientOptions.retryDelay = delay;
  }

  // ==================== ERROR HANDLING ====================

  addErrorHandler(handler: (error: ApiErrorResponse | Error) => void): void {
    this.events.onRequestError = handler;
  }

  removeErrorHandler(handler: (error: ApiErrorResponse | Error) => void): void {
    if (this.events.onRequestError === handler) {
      this.events.onRequestError = undefined;
    }
  }

  addErrorTransformer(transformer: (error: any) => ApiErrorResponse): void {
    // Error interceptor'a eklenebilir
    errorInterceptorManager.addInterceptor({
      intercept: async (error) => {
        const transformed = transformer(error);
        throw transformed;
      },
      shouldRetry: () => false,
      getPriority: () => 5
    });
  }

  // ==================== PRIVATE METHODS ====================

  private setupInterceptors(): void {
    // Response interceptor - error handling
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Error interceptor'ları çalıştır
        const result = await errorInterceptorManager.intercept(error);
        const startTime = error.config?.metadata?.startTime || Date.now();
        // Eğer retry döndürdüyse
        if (result === 'retry') {
          // Retry logic - orijinal config ile tekrar dene
          const originalRequest = error.config;
          originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
          
          // Exponential backoff delay
          const delay = this.calculateRetryDelay(
            originalRequest._retryCount,
            originalRequest.retryDelay || this.clientOptions.retryDelay
          );
          
          await this.delay(delay);
          return this.instance.request(originalRequest);
        }
        
        // Auth error interceptor
        await authInterceptor.onError(error);
        
        // Log error
        loggingInterceptorManager.logError(error, error.config);
        
        // Event handler çağır
        if (this.events.onRequestError) {
          const apiError = this.normalizeError(error);
          const metrics: RequestMetrics = {
            url: error.config?.url || 'unknown',
            method: error.config?.method?.toUpperCase() || 'GET',
            startTime,
            endTime: Date.now(),
            duration: Date.now() - startTime,
            status: error.response?.status,
            success: false
          };
          this.events.onRequestError(apiError, metrics);
        }
        
       throw error;
      }
    );
  }

  private normalizeToError(error: any): Error | ApiErrorResponse {
    if (error instanceof Error) {
      return error;
    }
    
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as any; // Type assertion
      return {
        message: responseData?.message || axiosError.message,
        status: axiosError.response?.status || 0,
        error: responseData?.error || 'Network Error',
        timestamp: new Date().toISOString(),
        path: axiosError.config?.url,
        validationErrors: responseData?.validationErrors,
        code: responseData?.code || axiosError.code
      };
    }
    
    // String veya diğer primitive tipleri Error'a çevir
    if (typeof error === 'string') {
      return new Error(error);
    }
    
    if (typeof error === 'object' && error !== null) {
      // Error-like object
      if (error.message) {
        const err = new Error(error.message);
        if (error.stack) err.stack = error.stack;
        if (error.name) err.name = error.name;
        return err;
      }
    }
    
    // Fallback
    return new Error('Unknown error occurred');
  }

  private convertToAxiosConfig(config: HttpRequestConfig): AxiosRequestConfig {
    const axiosConfig: AxiosRequestConfig = {
      ...config,
      method: config.method as Method,
      headers: config.headers,
      data: config.data,
      params: config.params,
      timeout: config.timeout,
      withCredentials: config.withCredentials,
      responseType: config.responseType,
      onUploadProgress: config.onUploadProgress,
      onDownloadProgress: config.onDownloadProgress,
      cancelToken: config.cancelToken
    };

    // Cancel token yoksa oluştur
    if (!axiosConfig.cancelToken && config.cancelable !== false) {
      const { token } = this.createCancelToken();
      axiosConfig.cancelToken = token;
    }

    return axiosConfig;
  }

  private createHttpResponse<T>(axiosResponse: AxiosResponse<T>): HttpResponse<T> {
    const normalizedHeaders = this.normalizeHeaders(axiosResponse.headers);
    return {
      data: axiosResponse.data,
      status: axiosResponse.status,
      statusText: axiosResponse.statusText,
      headers: normalizedHeaders,
      config: axiosResponse.config as HttpRequestConfig,
      request: axiosResponse.request
    };
  }

  private normalizeHeaders(headers: any): Record<string, string> {
  const normalized: Record<string, string> = {};
  
  if (!headers) return normalized;
  
  // Axios headers'ını işle
  for (const [key, value] of Object.entries(headers)) {
    if (value === null || value === undefined) {
      continue;
    }
    
    if (typeof value === 'string') {
      normalized[key] = value;
    } else if (Array.isArray(value)) {
      normalized[key] = value.join(', ');
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = String(value);
    } else if (typeof value === 'object') {
      // Nested object durumu için
      normalized[key] = JSON.stringify(value);
    }
  }
  
  return normalized;
}

  private async handleRequestError(error: any, config: HttpRequestConfig): Promise<never> {
    // Log error
    loggingInterceptorManager.logError(error, config);
    
    // Event handler çağır
    if (this.events.onRequestError) {
      const apiError = this.normalizeError(error);
      const metrics: RequestMetrics = {
        url: config.url || 'unknown',
        method: config.method?.toUpperCase() || 'GET',
        startTime: Date.now(),
        endTime: Date.now(),
        duration: 0,
        status: error.response?.status,
        success: false
      };
      this.events.onRequestError(apiError, metrics);
    }
    
    throw error;
  }

  private normalizeError(error: any): ApiErrorResponse {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as any; // Type assertion

      return {
        message: responseData?.message || axiosError.message,
        status: axiosError.response?.status || 0,
        error: responseData?.error || 'Network Error',
        timestamp: new Date().toISOString(),
        path: axiosError.config?.url,
        validationErrors: responseData?.validationErrors,
        code: responseData?.code || axiosError.code
      };
    }
    
    // Error nesnesini ApiErrorResponse'a çevir
    if (error instanceof Error) {
      return {
        message: error.message,
        status: 0,
        error: error.name || 'Unknown Error',
        timestamp: new Date().toISOString(),
        path: undefined,
        validationErrors: undefined,
        code: undefined
      };
    }
    
    // String veya diğer primitive tipleri
    return {
      message: String(error),
      status: 0,
      error: 'Unknown Error',
      timestamp: new Date().toISOString(),
      path: undefined,
      validationErrors: undefined,
      code: undefined
    };
  }  

  private extractPaginationMeta(response: HttpResponse): PaginationMeta {
    const headers = response.headers;
    
    return {
      total: Number.parseInt(headers['x-total-count'] || '0'),
      page: Number.parseInt(headers['x-page'] || '1'),
      limit: Number.parseInt(headers['x-limit'] || '10'),
      totalPages: Number.parseInt(headers['x-total-pages'] || '1'),
      hasNext: headers['x-has-next'] === 'true',
      hasPrev: headers['x-has-prev'] === 'true',
      sortBy: headers['x-sort-by'] || 'id',
      sortOrder: (headers['x-sort-order'] as 'asc' | 'desc') || 'asc'
    };
  }

  private calculateRetryDelay(retryCount: number, baseDelay: number): number {
    // Exponential backoff with jitter
    const exponentialDelay = baseDelay * Math.pow(2, retryCount - 1);
    const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
    return exponentialDelay + jitter;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * HttpClient Factory
 */
export class HttpClientFactory {
  private static instance: AxiosHttpClient;

  static createClient(options?: HttpClientOptions): AxiosHttpClient {
    return new AxiosHttpClient(options);
  }

  static getInstance(): AxiosHttpClient {
    if (!HttpClientFactory.instance) {
      HttpClientFactory.instance = new AxiosHttpClient();
    }
    
    return HttpClientFactory.instance;
  }
}

// Singleton instance export
export const httpClient = HttpClientFactory.getInstance();

// Default export
export default httpClient;